import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';

/**
 * Global search typeahead — spec 7.3, ported from the v18 block (:6225-6437).
 *
 * Live on every keystroke, NOT Enter-only. Searches courses, categories,
 * instructors, articles, resources, bundles and books; grouped by type;
 * matched text highlighted; ranking prefix > word-start > substring (done
 * server-side, see services/search.ts); ↑/↓/Enter/Escape; recent + trending
 * when empty; "See all results" footer; no-results state.
 *
 * The dropdown MUST NOT inherit the input's width — the ported CSS handles it
 * (v18.css:3): width:max(100%,440px); max-width:min(92vw,560px); right:0.
 * Visibility is the `open` class, so the box is ALWAYS rendered — same
 * contract as the mega-menu panel, and conditionally rendering it would leave
 * it display:none while mounted.
 *
 * The prototype ran `eval(el.dataset.act)` on click (:6345). Here every result
 * carries a plain href and the client navigates — no eval, no code strings in
 * the DOM.
 */

interface Suggestion {
  group: string;
  icon: string;
  label: string;
  sub: string;
  href: string;
}

interface SearchResponse {
  query: string;
  results: Suggestion[];
  trending: string[];
}

const RECENT_KEY = 'maktech_recent_searches';

/** recentSearches()/pushRecent() (:6288) — max 5, case-insensitive dedupe. */
function readRecent(): string[] {
  try {
    const v = localStorage.getItem(RECENT_KEY);
    return v ? (JSON.parse(v) as string[]) : [];
  } catch {
    return [];
  }
}
function pushRecent(q: string): void {
  const t = q.trim();
  if (!t) return;
  try {
    const r = [t, ...readRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(r));
  } catch {
    /* private mode — a search box is not worth crashing over */
  }
}

/**
 * sgHL() (:6281) — highlight the matched run.
 *
 * The prototype escapes BEFORE searching, then builds an HTML string. Here
 * React escapes on render, so the text is SPLIT into plain strings and a
 * <mark>. Same result, and there is no HTML string to get wrong: a user typing
 * `<img onerror=...>` is text at every step, never markup.
 */
function Highlight({ text, q }: { text: string; q: string }) {
  const needle = q.trim();
  if (!needle) return <>{text}</>;
  const i = text.toLowerCase().indexOf(needle.toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark className="sg-hl">{text.slice(i, i + needle.length)}</mark>
      {text.slice(i + needle.length)}
    </>
  );
}

export function SearchBox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(-1);
  const [recent, setRecent] = useState<string[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce: the prototype filtered in-memory so it could run per keystroke.
  // This hits the network, so a 150ms pause turns "javascript" from 10
  // requests into 1 without any perceptible lag.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 150);
    return () => clearTimeout(t);
  }, [value]);

  const { data } = useQuery({
    queryKey: ['search', debounced],
    queryFn: async () => (await api.get<SearchResponse>(`/search?q=${encodeURIComponent(debounced)}`)).data,
    enabled: open,
    staleTime: 60_000,
  });

  // closeSuggest (:6350) — click-away.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const runSearch = useCallback(
    (q: string) => {
      const t = q.trim();
      // runSearch (:6354) opens with `if(inp)inp.value=q` — the term STAYS in
      // the box after searching. It matters for the two callers that don't
      // already have it there: a trending chip and a recent-search row both put
      // their own text into the input.
      setValue(t);
      pushRecent(t);
      setRecent(readRecent());
      setOpen(false);
      setIndex(-1);
      navigate(t ? `/courses?search=${encodeURIComponent(t)}` : '/courses');
    },
    [navigate],
  );

  const goTo = useCallback(
    (href: string) => {
      pushRecent(value);
      setRecent(readRecent());
      setOpen(false);
      setIndex(-1);
      navigate(href);
    },
    [navigate, value],
  );

  const results = data?.results ?? [];
  const trending = data?.trending ?? [];
  const groups = [...new Set(results.map((r) => r.group))];
  const hasQuery = debounced.trim().length > 0;

  /**
   * The flat keyboard list. It must match the RENDER order exactly, or ↓ moves
   * the highlight to a different row than the one Enter activates.
   */
  const flat: { kind: 'recent' | 'result' | 'all'; label: string; href?: string }[] = hasQuery
    ? results.length
      ? [
          ...groups.flatMap((g) =>
            results.filter((r) => r.group === g).map((r) => ({ kind: 'result' as const, label: r.label, href: r.href })),
          ),
          { kind: 'all' as const, label: debounced },
        ]
      : []
    : recent.map((r) => ({ kind: 'recent' as const, label: r }));

  function activate(i: number) {
    const item = flat[i];
    if (!item) return;
    if (item.kind === 'result' && item.href) goTo(item.href);
    else runSearch(item.label);
  }

  // suggestKey (:6367)
  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setIndex((i) => (flat.length ? (i + 1 + flat.length) % flat.length : -1));
      return;
    }
    /**
     * PROTOTYPE BUG, PORTED AS-IS — reported, not silently fixed.
     *
     * moveSuggest (:6363) is `sgIndex=(sgIndex+d+n)%n` and sgIndex starts at
     * -1, so ArrowUp on a fresh dropdown computes (-1-1+n)%n = n-2 and lands on
     * the SECOND-to-last row, not the last. (ArrowDown is fine: (-1+1+n)%n = 0.)
     * Spec 7.3 only says "Keyboard: ↑/↓/Enter/Escape" — it does not specify wrap
     * semantics, so there is no spec/prototype conflict for the spec to win, and
     * the brief is explicit that the prototype defines behaviour. A one-line
     * `i <= -1 ? flat.length - 1 : ...` fixes it whenever you want it fixed.
     */
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => (flat.length ? (i - 1 + flat.length) % flat.length : -1));
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setIndex(-1);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'Enter') {
      if (index >= 0 && flat[index]) {
        e.preventDefault();
        activate(index);
        return;
      }
      runSearch(value);
    }
  }

  let cursor = -1;

  return (
    <div className="nav-search" ref={wrapRef}>
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" />
      </svg>
      <input
        id="navSearch"
        data-testid="nav-search"
        type="text"
        autoComplete="off"
        placeholder={t('nav.search_placeholder')}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setIndex(-1);
          setOpen(true);
        }}
        onFocus={() => {
          setRecent(readRecent());
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={open}
        aria-controls="sgBox"
        aria-autocomplete="list"
      />

      {/* ALWAYS rendered; `open` drives visibility (v18.css:7). */}
      <div className={`sg-box ${open ? 'open' : ''}`} id="sgBox" data-testid="sg-box" role="listbox">
        {!hasQuery ? (
          <>
            {recent.length ? (
              <div className="sg-group">
                <span className="sg-gh">
                  Recent searches
                  <button
                    className="sg-clear"
                    data-testid="sg-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.removeItem(RECENT_KEY);
                      setRecent([]);
                    }}
                  >
                    Clear
                  </button>
                </span>
                {recent.map((r) => {
                  cursor++;
                  const mine = cursor;
                  return (
                    <button
                      className={`sg-item ${index === mine ? 'on' : ''}`}
                      key={r}
                      data-testid={`sg-recent-${r}`}
                      onClick={() => runSearch(r)}
                    >
                      <span className="sg-ico">🕘</span>
                      <span className="sg-txt">
                        <b>{r}</b>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
            <div className="sg-group">
              <span className="sg-gh">Trending</span>
              <div className="sg-chips">
                {(trending.length ? trending : ['React', 'Python', 'IELTS', 'Excel', 'Free courses', 'UI/UX']).map((t) => (
                  <button className="sg-chip" key={t} data-testid={`sg-trend-${t}`} onClick={() => runSearch(t)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : results.length === 0 ? (
          <div className="sg-empty" data-testid="sg-empty">
            <div className="sg-empty-ico">🔍</div>
            <b>No matches for "{debounced}"</b>
            <p>Try a broader term, or browse everything we teach.</p>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setOpen(false);
                navigate('/courses');
              }}
            >
              Browse all courses
            </button>
          </div>
        ) : (
          <>
            {groups.map((g) => (
              <div className="sg-group" key={g}>
                <span className="sg-gh">{g}</span>
                {results
                  .filter((r) => r.group === g)
                  .map((r) => {
                    cursor++;
                    const mine = cursor;
                    return (
                      <button
                        className={`sg-item ${index === mine ? 'on' : ''}`}
                        key={`${r.group}-${r.href}-${r.label}`}
                        data-testid={`sg-item-${r.label}`}
                        role="option"
                        aria-selected={index === mine}
                        onClick={() => goTo(r.href)}
                      >
                        <span className="sg-ico">{r.icon}</span>
                        <span className="sg-txt">
                          <b>
                            <Highlight text={r.label} q={debounced} />
                          </b>
                          <span>{r.sub}</span>
                        </span>
                        <span className="sg-go">↵</span>
                      </button>
                    );
                  })}
              </div>
            ))}
            {(() => {
              cursor++;
              const mine = cursor;
              return (
                <button
                  className={`sg-all ${index === mine ? 'on' : ''}`}
                  data-testid="sg-all"
                  onClick={() => runSearch(debounced)}
                >
                  See all results for "<b>{debounced}</b>" →
                </button>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}
